import React, { FunctionComponent, memo, useRef, useEffect, useState } from 'react';
import { Link as GatsbyLink } from 'gatsby';
import {
  ILink,
  ActionDownload,
  ActionScroll,
  ActionIntercomBot,
  ActionPopup,
  IAction,
  externalHrefType,
  internalHrefType,
} from '../declarations/index';
import { scrollto, ensureTrailingSlash } from 'bit/shared/utils/index';
import { getHrefValueFromAction, checkLinkTarget, postOrPageHref, getIndexOfPopup } from './utils';
import useIsClient from '../../../src/hooks/useIsClient';

declare global {
  interface Window {
    gtag: (arg1: any, arg2: any, arg3: any) => void | undefined;
  }
}

interface ILinkProxy extends ILink {
  to?: string | null;
  children?: React.ReactNode | null;
  updateGlobal?: (args: any) => void;
  [key: string]: unknown;
  googleAnalyticsEvent?: boolean | null;
  gtagEvent?: string | null;
  gtagCategory?: string | null;
  gtagLabel?: string | null;
}

const Link: FunctionComponent<ILinkProxy> = memo(
  ({
    to,
    displayText1,
    displayText2,
    externalHref,
    internalHref,
    external,
    forceNativeNavigation,
    action,
    useActionInstead,
    children,
    googleAnalyticsEvent,
    gtagEvent,
    gtagCategory,
    gtagLabel,
    updateGlobal,
    keepQuery,
    id,
    ...props
  }) => {
    const consultationLinksIds = [
      '78935143',
      '78935551',
      'MyaXcMCrTVu--qjQu7650Q',
      '78937174',
      'AE4NncO7S6yp9vH5QjZBHA',
      '121695724',
      '78937187',
      'F8tXAubLS36B4v4IxjVLFg',
      '113329816',
      '152123963',
      '78935858',
      '78935154',
    ];
    const isConsultationLink = consultationLinksIds.some((el) => {
      return id?.includes(el);
    });
    if (isConsultationLink && process.env.GATSBY_LOCALE === 'en-UK') {
      return null;
    }
    const isClient = useIsClient();
    const [retainedQueryParams, setRetainedQueryParams] = useState('');

    useEffect(() => {
      if (isClient && keepQuery) {
        const r = extractNonBlacklistedQueryParams(window.location.search);
        setRetainedQueryParams(r);
      }
    }, [isClient, keepQuery]);

    const href = getHref({ external, externalHref, internalHref });
    const { _action, isDownload, isIntercomBot, isPopup, isScroll } = getAction(action);
    const finalHref = postOrPageHref(href);
    const hrefValue = useActionInstead && action && getHrefValueFromAction(action);
    const navigationLink = useActionInstead ? hrefValue : to || finalHref;
    const isExternal = checkLinkTarget(navigationLink);
    const scrollPageSlug = isScroll ? _action?.page?.slug : null;

    // For Accessibility
    const popupRef = useRef<HTMLButtonElement | null>(null);

    if (!navigationLink) return null;

    if (isPopup && updateGlobal) {
      const popupData = _action.popup;
      return (
        <button
          {...props}
          ref={popupRef}
          data-popup-id={popupData?.id}
          onClick={() => {
            try {
              if (window.gtag && googleAnalyticsEvent) {
                window.gtag('event', gtagEvent, {
                  event_category: gtagCategory,
                  event_label: gtagLabel,
                });
              }
            } catch (error) {
              console.log(error);
            }
            const popupId = popupData?.id as string | undefined;
            const popupIndex = getIndexOfPopup(popupId, popupRef?.current);
            updateGlobal({ modal: true, popup: { ...popupData, popupId: popupData?.id, popupIndex } });
          }}
        >
          {children || displayText1}
        </button>
      );
    }

    if (isIntercomBot) {
      const botSelector = _action.botSelector;
      return (
        <button
          {...props}
          onClick={() => {
            try {
              if (window.gtag && googleAnalyticsEvent) {
                window.gtag('event', gtagEvent, {
                  event_category: gtagCategory,
                  event_label: gtagLabel,
                });
              }
            } catch (error) {
              console.log(error);
            }
          }}
          data-intercom-action={botSelector}
        >
          {children || displayText1}
        </button>
      );
    }

    if (forceNativeNavigation || isExternal || useActionInstead) {
      const linkWithTrailingSlash =
        navigationLink.slice(-1) !== '/' && navigationLink.charAt(0) === '/' ? `${navigationLink}/` : navigationLink;

      let thisPageSlug = '';
      if (isClient) {
        try {
          thisPageSlug = window.location.pathname;
        } catch (error) {}
      }

      function _nativeHref() {
        if (!isScroll) {
          return linkWithTrailingSlash;
        } else if (scrollPageSlug && ensureTrailingSlash(thisPageSlug) !== ensureTrailingSlash(scrollPageSlug)) {
          return `${scrollPageSlug}${linkWithTrailingSlash}`;
        } else {
          return undefined;
        }
      }

      return (
        <a
          {...props}
          href={_nativeHref() || props.href || undefined}
          download={isDownload ? _action.asset?.title : undefined}
          onClick={
            !_nativeHref()
              ? () => {
                  try {
                    if (window.gtag && googleAnalyticsEvent) {
                      window.gtag('event', gtagEvent, {
                        event_category: gtagCategory,
                        event_label: gtagLabel,
                      });
                    }
                  } catch (error) {
                    console.log(error);
                  }
                  scrollto(navigationLink?.replace('#', ''));
                }
              : () => {
                  try {
                    if (window.gtag && googleAnalyticsEvent) {
                      window.gtag('event', gtagEvent, {
                        event_category: gtagCategory,
                        event_label: gtagLabel,
                      });
                    }
                  } catch (error) {
                    console.log(error);
                  }
                }
          }
        >
          {children || displayText1}
        </a>
      );
    } else {
      const isProcedurePage = navigationLink.includes('/procedures/');
      const shouldKeep = keepQuery && shouldKeepQueryParams(navigationLink) && !isProcedurePage;
      const queryParams = shouldKeep ? retainedQueryParams : '';
      const linkWithTrailingSlash =
        navigationLink.slice(-1) === '/' ? `${navigationLink}${queryParams}` : `${navigationLink}/${queryParams}`;

      return (
        <GatsbyLink
          {...{
            to: cleanUrlParams(linkWithTrailingSlash, isProcedurePage),
            children: children || displayText1 || displayText2,
            ...props,
          }}
          onClick={() => {
            try {
              if (window.gtag && googleAnalyticsEvent) {
                window.gtag('event', gtagEvent, {
                  event_category: gtagCategory,
                  event_label: gtagLabel,
                });
              }
            } catch (error) {
              console.log(error);
            }
          }}
        />
      );
    }
  },
);

// Helper functions
function getHref({
  external,
  externalHref,
  internalHref,
}: {
  external: boolean | null | undefined;
  externalHref: externalHrefType;
  internalHref: internalHrefType | null | undefined;
}) {
  return external ? externalHref : internalHref || externalHref;
}

function getAction(action?: IAction | null) {
  const __typename = action?.[0]?.__typename;

  if (__typename === 'DatoCmsDownload' || __typename === 'DownloadRecord') {
    return {
      isDownload: true,
      _action: action?.[0] as ActionDownload,
    };
  }

  if (__typename === 'DatoCmsScroll' || __typename === 'ScrollRecord') {
    return {
      isScroll: true,
      _action: action?.[0] as ActionScroll,
    };
  }

  if (__typename === 'DatoCmsPopup' || __typename === 'PopupRecord') {
    return {
      isPopup: true,
      _action: action?.[0] as ActionPopup,
    };
  }

  if (__typename === 'DatoCmsIntercomBot' || __typename === 'IntercomBotRecord') {
    return {
      isIntercomBot: true,
      _action: action?.[0] as ActionIntercomBot,
    };
  }

  return {
    __typename,
    _action: action?.[0],
  };
}

function extractNonBlacklistedQueryParams(search: string): string {
  const BLACKLIST = new Set([
    'gender',
    'ageRange',
    'procedureType',
    'procedureArea',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'gl',
  ]);

  const searchParams = new URLSearchParams(search);
  const retainedParams = new URLSearchParams();

  Array.from(searchParams.entries()).forEach(([key, value]) => {
    if (!BLACKLIST.has(key) && value) {
      retainedParams.append(key, value);
    }
  });

  const result = retainedParams.toString();
  return result ? `?${result}` : '';
}

function shouldKeepQueryParams(url: string): boolean {
  const ALLOWED_PATHS = ['/contact/', '/appointment/', '/request-info/'];

  const EXCLUDED_PATHS = ['/procedures/'];

  return ALLOWED_PATHS.some((path) => url.includes(path)) && !EXCLUDED_PATHS.some((path) => url.includes(path));
}

function cleanUrlParams(url: string, isProcedurePage: boolean): string {
  if (isProcedurePage) {
    return url.split('?')[0]; // Elimina todos los parámetros para páginas de procedimientos
  }
  return url;
}

export { Link, ILinkProxy };
